import React, { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { bankCardMetadata } from "@features/Budget/budgetConstants";
import { useBudgetFunctionalityDisabledError } from "@features/Budget/hooks/useBudgetFunctionalityDisabledError";
import { Button } from "@components/Button";
import { PlusIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useCreateExpensePlanMutation } from "@services/budget/mutations";
import {
  useGetExpensePlanCategoriesQuery,
  useGetExpensePlanIntervalsQuery,
} from "@services/budget/queries";
import {
  type ExpensePlanInterval,
  type IExpensePlanBankAccountsResponse,
} from "@services/budget/types";
import { parseDecimalNumberInputValue } from "@utils/units";
import {
  useAddExpensePlanSchema,
  type TAddExpensePlanSchema,
} from "./useAddExpensePlanSchema";

interface IAddExpensePlanModalProps extends IUseModalProps {
  expensePlanBankAccounts: IExpensePlanBankAccountsResponse;
}

export const AddExpensePlanModal: React.FC<IAddExpensePlanModalProps> = (
  props,
) => {
  const { expensePlanBankAccounts, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: intervalData, isLoading: isIntervalDataLoading } =
    useGetExpensePlanIntervalsQuery();

  const { data: categoryData, isLoading: isCategoryDataLoading } =
    useGetExpensePlanCategoriesQuery();

  const intervalOptions = useMemo(
    () =>
      intervalData?.intervals.map((interval) => ({
        label: t(`budget.interval.${interval}`),
        value: interval,
      })),
    [intervalData, t],
  );

  const categoryOptions = useMemo(
    () =>
      categoryData?.transactionCategories
        .map((categories) => ({
          label:
            categories.customerCategoryName ??
            t(`budget.category.${categories.categorySlug}`),
          value: categories.transactionCategoryId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [categoryData, t],
  );

  const schema = useAddExpensePlanSchema();

  const { mutateAsync, isPending } = useCreateExpensePlanMutation();

  const { control, setValue, getValues, handleSubmit, setError, watch } =
    useForm<TAddExpensePlanSchema>({
      resolver: zodResolver(schema),
      defaultValues: {
        planName: "",
        planAmount: "",
        bankAccountId: 0,
      },
    });

  const bankAccountId = watch("bankAccountId");

  const currentBankIcon =
    bankCardMetadata[
      expensePlanBankAccounts.bankAccounts.find(
        (bank) => bank.bankAccountId === bankAccountId,
      )?.bankName ?? "Default"
    ]?.iconSmall;

  const bankSelectOptions = [
    {
      label: t("budget.expenses.addExpensePlanModal.allBankAccounts"),
      value: 0,
      icon: undefined,
    },
    ...expensePlanBankAccounts.bankAccounts.map((bank) => ({
      label: bank.iban,
      value: bank.bankAccountId,
      icon: bankCardMetadata[bank.bankName].iconSmall,
    })),
  ];

  const { setApiErrors } = useApiErrorUtils(setError);
  const { handleBudgetError } = useBudgetFunctionalityDisabledError();

  const onSubmit: SubmitHandler<TAddExpensePlanSchema> = async (data) => {
    const response = await mutateAsync({
      planAmount: parseDecimalNumberInputValue(data.planAmount.toString()),
      planName: data.planName,
      expensePlanIntervalId: data.expensePlanIntervalId as ExpensePlanInterval,
      transactionCategoryId: data.transactionCategoryId,
      bankAccountId: data.bankAccountId === 0 ? undefined : data.bankAccountId,
    }).catch(handleBudgetError(setApiErrors));

    if (response) {
      toast.success(t("budget.expenses.addExpensePlanModal.success"));
      modal.close();
    }

    setTimeout(() => {
      void queryClient.refetchQueries({ queryKey: ["budget", "expensePlans"] });
    }, 10_000);
  };

  return (
    <Modal
      modal={modal}
      title={t("budget.expenses.addExpensePlanModal.title")}
      subtitle={t("budget.expenses.addExpensePlanModal.subtitle")}
      badgeProps={{ icon: PlusIcon, color: "green" }}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("common.form.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <FormSelect
          control={control}
          name="transactionCategoryId"
          label={t("budget.expenses.addExpensePlanModal.transactionCategory")}
          isLoading={isCategoryDataLoading}
          options={categoryOptions ?? []}
        />
        <FormInput
          label={t("budget.expenses.addExpensePlanModal.planName")}
          control={control}
          name="planName"
        />
        <FormInput
          label={t("budget.expenses.addExpensePlanModal.planAmmount")}
          control={control}
          name="planAmount"
          onBlur={() =>
            setValue(
              "planAmount",
              parseDecimalNumberInputValue(getValues("planAmount").toString()),
            )
          }
        />
        <FormSelect
          label={t("budget.expenses.addExpensePlanModal.bankAccount")}
          name="bankAccountId"
          control={control}
          options={bankSelectOptions}
          icon={currentBankIcon}
        />
        <FormSelect
          control={control}
          name="expensePlanIntervalId"
          label={t("budget.expenses.addExpensePlanModal.interval")}
          isLoading={isIntervalDataLoading}
          options={intervalOptions ?? []}
        />
      </div>
    </Modal>
  );
};
